import type {
  AbstractPartDefinition,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  Sides,
} from "@project/emerge-types";
import type { Euler, Vector3 } from "three";
import { rotateOntoSide } from "../utils/RotationUtils";

export type PartResponse<
  BlueprintType,
  SettingsType,
  PartInputType,
  Kind extends string,
  DefinitionType extends AbstractPartDefinition<string>,
> = Record<
  string,
  {
    kind: Kind;
    selector: (
      blueprint: BlueprintType,
      settings: SettingsType,
    ) => PartInputType;
    createPart: (input: PartInputType) => DefinitionType;
  }
>;

/**
 * AbstractBuildstep provides a framework for asynchronous build steps.
 * Each build step processes a blueprint with given settings and asynchronously yields part definitions.
 *
 * BlueprintType, SettingsType, and PartType can be further typed in concrete implementations.
 */
export abstract class AbstractBuildstep<
  BlueprintType = unknown,
  SettingsType = unknown,
  PartType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
  PartDependencyData = unknown,
> {
  public buildstepName: string;

  constructor(buildstepName: string) {
    this.buildstepName = buildstepName;
  }

  public getPartResponse?(): PartResponse<
    BlueprintType,
    SettingsType,
    PartType,
    string,
    PartType
  >;

  /**
   * @deprecated Prefer the selectDependencies/createPart pattern for incremental updates.
   * Execute the build step asynchronously.
   * This async generator should yield part definitions one at a time.
   *
   * @param blueprint the blueprint used as a reference for building parts.
   * @param settings  the configuration or settings used for building.
   * @param parts     an array containing parts built so far (if needed).
   *
   * @yields PartType each time a new part is built.
   */
  public execute?(
    blueprint: BlueprintType,
    settings: SettingsType,
    parts: PartType[],
  ): AsyncGenerator<PartType, void, unknown>;

  /**
   * Selects the dependency data required to build each part managed by this build step.
   * This method should be fast and avoid heavy computations.
   *
   * @param blueprint The blueprint reference.
   * @param settings The configuration settings.
   * @returns A map where keys are stable, unique part IDs and values are the calculated dependency data.
   */
  public abstract selectDependencies(
    blueprint: BlueprintType,
    settings: SettingsType,
  ): Map<string, PartDependencyData>;

  /**
   * Creates a single part definition based on the provided dependency data.
   * This method should be a pure function of its inputs and not rely on external blueprint or settings.
   *
   * @param partId The unique identifier for the part being created.
   * @param dependencies The dependency data calculated by selectDependencies.
   * @returns The constructed part definition.
   */
  public abstract createPart(
    partId: string,
    dependencies: PartDependencyData,
  ): PartType;

  public rotateOntoSide(side: Sides, orientation = 0): Euler {
    return rotateOntoSide(side, orientation);
  }

  /**
   * Convert a drag position change back to settings updates.
   * This method is called when a draggable part is released after being moved.
   *
   * Override this method in buildsteps that produce draggable parts.
   *
   * @param partId - The ID of the dragged part.
   * @param newPosition - The new world position after drag.
   * @param constraint - The drag constraint from the part definition.
   * @param settingsContext - The serializable context data from the part definition.
   * @param currentSettings - The current product settings.
   * @returns DragUpdateResult with settings and display values, or null if the position is invalid.
   */
  public updateSettingsFromDrag?(
    partId: string,
    newPosition: Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: SettingsType,
    options?: DragUpdateOptions,
  ): DragUpdateResult<SettingsType> | null;
}
